/**
 * CalendarAddActions: 予約完了後のカード内に表示する「ICSファイルをダウンロード」
 * 「Googleカレンダーに追加」リンク。design-mockups/*.html準拠（2026-08-19デザイン更新）。
 *
 * <p>従来は別モーダル（`AppointmentConfirmationView`）内で表示していたが、デザイン更新により
 * 予約確定・リクエスト送信完了時はカード内を完了メッセージへ差し替える方式に変更したため、
 * このロジックをPreBookingForm・MultiCvAppointmentList双方から利用する共通ヘルパーとして分離した。
 * ICS発行はhc-api規約がPOST固定のため、fetch+Blob+一時<a>要素方式でダウンロードする
 * （`appointmentConfirmationUi.ts`の旧実装を移設）。
 */

import { createElement } from "../core/shadowDomUtil";
import { relayClient } from "../core/relayClient";
import { formatGoogleCalendarDate } from "./datetime";

export const CALENDAR_ADD_ACTIONS_STYLES = `
.spica-calendar-pending-notice {
  margin: 10px 0 0; padding: 8px 10px; border-radius: var(--spica-radius-default, 4px);
  background: var(--spica-surface-subtle, #f9f9f9); border: 1px solid var(--spica-border-default, #e6e6e6);
  color: var(--spica-text-secondary, #666666); font-size: 12px; line-height: 1.6;
}
.spica-calendar-add-actions { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
.spica-calendar-add-action {
  display: inline-flex; align-items: center; justify-content: center;
  box-sizing: border-box; min-height: 32px; border: 1px solid var(--spica-border-default, #e6e6e6);
  border-radius: var(--spica-radius-default, 4px); padding: 6px 10px; color: var(--spica-text-primary, #333333);
  background: #fff; font-size: 12px; font-weight: 700; line-height: 1.4; text-decoration: none;
}
.spica-calendar-add-action:hover { border-color: var(--spica-text-primary, #333333); background: var(--spica-surface-subtle, #f9f9f9); }
.spica-calendar-add-action:focus-visible { outline: 2px solid var(--spica-text-primary, #333333); outline-offset: 2px; }

.spica-calendar-ics-help { margin: 8px 0 0; font-size: 11px; line-height: 1.6; color: var(--spica-text-secondary, #666666); }
.spica-calendar-ics-help-steps { margin: 4px 0 0; padding: 0; list-style: none; }
.spica-calendar-ics-help-steps li { padding-left: 1.5em; text-indent: -1.5em; }
.spica-calendar-ics-help-steps li + li { margin-top: 2px; }
`;

export interface CalendarAddActionsInfo {
  appointmentId: string;
  scheduledStartAt: string;
  scheduledEndAt: string;
  exhibitorCompanyName?: string | null;
}

/**
 * 「ICSファイルをダウンロード」「Googleカレンダーに追加」の2リンクと、ICSの使い方補足、
 * および「アポイント未確定（相手企業の連絡待ち）」の注意文言を含むコンテナ要素を構築する。
 * 日時未確定（アポイントリクエストで完了した場合）は呼び出し元がこの関数自体を呼ばない想定。
 */
export function renderCalendarAddActions(info: CalendarAddActionsInfo): HTMLElement {
  // ボタン群＋補足メッセージをまとめるラッパー（呼び出し元はこの1要素をappendする）。
  const container = createElement("div", { className: "spica-calendar-add" });

  // アポイント未確定の注意文言（営業要望、2026-09-11）。日時を指定して送信した場合でも、この時点では
  // アポイントは確定しておらず相手企業（出展企業）からの連絡待ちである旨を明示し、ICSダウンロード画面を
  // 「確定済み」と誤認させないようにする。ICS/Googleカレンダー追加リンクの直上に表示する。
  container.appendChild(
    createElement("p", {
      className: "spica-calendar-pending-notice",
      text: "現時点ではアポイントは確定しておりません。相手企業からのご連絡をお待ちください。",
    })
  );

  const actions = createElement("div", { className: "spica-calendar-add-actions" });

  // ICS発行はhc-api規約がPOST固定のため`<a href>`直接遷移は使えず、クリックをフックして
  // fetch+Blobでダウンロードする（href="#"はフォールバック時の遷移を防ぐためpreventDefaultと併用）。
  const icsLink = createElement("a", {
    className: "spica-calendar-add-action",
    text: "ICSファイルをダウンロード",
    attrs: { href: "#", role: "button" },
  });
  icsLink.addEventListener("click", (event) => {
    event.preventDefault();
    void downloadIcs(info.appointmentId);
  });
  actions.appendChild(icsLink);

  const googleLink = createElement("a", {
    className: "spica-calendar-add-action",
    text: "Googleカレンダーに追加",
    attrs: { target: "_blank", rel: "noopener noreferrer", href: buildGoogleCalendarUrl(info) },
  });
  actions.appendChild(googleLink);

  container.appendChild(actions);
  container.appendChild(renderIcsHelp());

  return container;
}

/**
 * ICSファイルの使い方がわからないユーザー（ITリテラシーの低い製造業層）向けの補足説明を構築する。
 * Googleカレンダー以外の利用者に向けて、ICSダウンロードの手順を2ステップで簡潔に示す。
 */
function renderIcsHelp(): HTMLElement {
  const help = createElement("p", { className: "spica-calendar-ics-help" });
  help.appendChild(
    document.createTextNode(
      "Googleカレンダー以外をお使いの方は「ICSファイルをダウンロード」から登録できます。"
    )
  );

  const steps = createElement("ol", { className: "spica-calendar-ics-help-steps" });
  for (const stepText of [
    "1. ファイルをダウンロード",
    "2. 開くと予定が追加されます",
  ]) {
    steps.appendChild(createElement("li", { text: stepText }));
  }
  help.appendChild(steps);

  return help;
}

async function downloadIcs(appointmentId: string): Promise<void> {
  // 2026-09-09修正: relayClient.getIcsはICSの生テキスト（text/calendar）をそのまま
  // response.dataに文字列として返す（relayClient.ts#post のContent-Type分岐参照）。
  // 従来はJSONレスポンス前提で`instanceof Blob`判定をしていたが、実際にはBlobにも
  // ならずJSONパース自体が失敗してdataがnullになり、ダウンロードが実行されない不具合があった。
  const response = await relayClient.getIcs<string>({ id: appointmentId });
  if (!response.ok || !response.data) return;

  const blob = new Blob([response.data], { type: "text/calendar" });
  const url = URL.createObjectURL(blob);
  const link = createElement("a", { attrs: { href: url, download: "appointment.ics" } });
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function buildGoogleCalendarUrl(info: CalendarAddActionsInfo): string {
  const start = formatGCalDate(info.scheduledStartAt);
  const end = formatGCalDate(info.scheduledEndAt);
  const title = info.exhibitorCompanyName ? `${info.exhibitorCompanyName}様とのアポイント` : "アポイント";
  const text = encodeURIComponent(title);
  // 日時はJST壁時計値のため、Googleカレンダー側でJST解釈させるよう ctz=Asia/Tokyo を明示する
  // （Zを付与しない。datetime-contract.md §6 / frontend-display-spec.md §4）。
  return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${start}/${end}&ctz=Asia/Tokyo`;
}

/**
 * JSTローカル日時文字列（`yyyy-MM-ddTHH:mm:ss`、オフセットなし）をGoogleカレンダーのURL形式
 * （`YYYYMMDDTHHMMSS`、**Zを付与しない**）へ変換する。
 *
 * <p><b>U16（DATETIME JST統一）</b>: 受信する日時はJSTの壁時計値になったため、末尾に `Z`（UTC）を
 * 付与すると9時間ずれる。Zの付与を廃止し、JST壁時計値をそのまま整形して `ctz=Asia/Tokyo` と組み合わせる
 * （datetime-contract.md §6）。文字列操作のみで整形し `new Date(iso)` は使わない。
 */
function formatGCalDate(iso: string): string {
  return formatGoogleCalendarDate(iso);
}
